import React, { useState } from "react";
import "./CollectionReport.css";
import {
  FaCalendarAlt,
  FaWallet,
  FaMoneyBillAlt,
  FaCreditCard,
  FaMobileAlt,
  FaReceipt
} from "react-icons/fa";
import { Pagination } from "@mui/material";

const DaywiseCollection = ({ data = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  if (!data || data.length === 0) {
    return <div className="no-data">No daywise collection data available for the selected range.</div>;
  }

  // Calculate total collection figure across all dates in range
  const totalRangeCollected = data.reduce((acc, curr) => acc + (curr.totalCollected || 0), 0);
  const totalRangePayments = data.reduce((acc, curr) => acc + (curr.totalPayments || 0), 0);

  const totalPages = Math.ceil(data.length / itemsPerPage) || 1;
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="daywise-collection-container">
      {/* Total Collection KPI Card for Selected Date Range */}
      <div className="daywise-summary-banner">
        <div className="summary-banner-card">
          <div className="banner-icon-box">
            <FaWallet size={20} color="#16a34a" />
          </div>
          <div className="banner-details">
            <span className="banner-subtitle">SELECTED RANGE TOTAL COLLECTION</span>
            <h2 className="banner-amount">
              ₹ {totalRangeCollected.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
          </div>
        </div>

        <div className="summary-banner-meta">
          <span className="meta-pill">
            <FaCalendarAlt size={12} /> {data.length} Days in Range
          </span>
          <span className="meta-pill">
            <FaReceipt size={12} /> {totalRangePayments} Total Transactions
          </span>
        </div>
      </div>

      {/* Daywise Table */}
      <div className="transactions-section">
        <div className="transactions-section-header">
          <div className="header-title-group">
            <FaCalendarAlt size={16} color="#2563eb" />
            <h3 className="table-title">Daywise Collection Breakdown</h3>
          </div>
          <span className="count-pill-badge">{data.length} Days Listed</span>
        </div>

        <div className="table-responsive">
          <table className="report-data-table">
            <thead>
              <tr>
                <th>
                  <div className="th-flex">
                    <FaCalendarAlt size={11} color="#64748b" />
                    <span>DATE</span>
                  </div>
                </th>
                <th className="text-center">
                  <div className="th-flex justify-center">
                    <FaReceipt size={11} color="#64748b" />
                    <span>TRANSACTIONS</span>
                  </div>
                </th>
                <th className="text-right">
                  <div className="th-flex right-justify">
                    <FaWallet size={11} color="#64748b" />
                    <span>DAY COLLECTION</span>
                  </div>
                </th>
                <th className="text-right">
                  <div className="th-flex right-justify">
                    <FaMoneyBillAlt size={11} color="#d97706" />
                    <span>CASH</span>
                  </div>
                </th>
                <th className="text-right">
                  <div className="th-flex right-justify">
                    <FaCreditCard size={11} color="#0284c7" />
                    <span>CARD</span>
                  </div>
                </th>
                <th className="text-right">
                  <div className="th-flex right-justify">
                    <FaMobileAlt size={11} color="#16a34a" />
                    <span>UPI</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, index) => {
                const dayTotal = row.totalCollected || 0;
                const cashAmt = row.cash || 0;
                const cardAmt = row.card || 0;
                const upiAmt = row.upi || 0;

                return (
                  <tr key={index}>
                    <td>
                      <span className="date-pill-tag">{row.date}</span>
                    </td>
                    <td className="text-center">
                      <span className="count-pill-badge">{row.totalPayments}</span>
                    </td>
                    <td className="text-right">
                      <span className="amount-val paid-val">
                        ₹ {dayTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`split-amt ${cashAmt > 0 ? "cash-text" : "zero-text"}`}>
                        ₹ {cashAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`split-amt ${cardAmt > 0 ? "card-text" : "zero-text"}`}>
                        ₹ {cardAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`split-amt ${upiAmt > 0 ? "upi-text" : "zero-text"}`}>
                        ₹ {upiAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="table-pagination-footer">
          <span className="pagination-info-text">
            Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{" "}
            <strong>{Math.min(currentPage * itemsPerPage, data.length)}</strong> of{" "}
            <strong>{data.length}</strong> days
          </span>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            variant="outlined"
            shape="rounded"
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default DaywiseCollection;
